import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { positionService, Position } from '../services/positionService';
import { websocketService } from '../services/websocket';
import PositionCard from '../components/position/PositionCard';
import OrderForm from '../components/trade/OrderForm';
import { Preset, PriceType, LimitPriceType } from '../types/preset';
import { toast } from 'react-toastify';

// Extend Position type to include symbol (temporarily until API returns it)
interface ExtendedPosition extends Position {
  symbol?: string;
}

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 1rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h1`
  font-size: 1.8rem;
  margin: 0;
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #333;
  margin-bottom: 1.5rem;
`;

const Tab = styled.div<{ active: boolean }>`
  padding: 0.5rem 1rem;
  cursor: pointer;
  color: ${(props) => (props.active ? '#fff' : '#999')};
  font-weight: ${(props) => (props.active ? '600' : '400')};
  border-bottom: 2px solid ${(props) => (props.active ? '#3b82f6' : 'transparent')};
  
  &:hover {
    color: ${(props) => (props.active ? '#fff' : '#ccc')};
  }
`;

const PositionsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1rem;
`;

const LoadingText = styled.div`
  text-align: center;
  color: #999;
  font-style: italic;
  padding: 2rem 0;
`;

const ErrorText = styled.div`
  color: ${({ theme }) => theme.colors.error.main};
  text-align: center;
  padding: 1rem;
`;

const TestControls = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const TestButton = styled.button`
  background-color: #3b82f6;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-size: 0.9rem;
  
  &:hover {
    background-color: #2563eb;
  }
  
  &:disabled {
    background-color: #4b5563;
    cursor: not-allowed;
  }
`;

const TestCard = styled.div`
  padding: 1rem;
  background-color: #262626;
  border-radius: 8px;
  border: 1px solid #333;
  margin-bottom: 1.5rem;
`;

const TestCardTitle = styled.h3`
  font-size: 1.2rem;
  margin: 0 0 0.5rem 0;
  color: #f9fafb;
`;

const TestDescription = styled.p`
  font-size: 0.9rem;
  color: #9ca3af;
  margin: 0 0 1rem 0;
`;

const FormContainer = styled.div`
  background-color: #1e1e1e;
  border-radius: 8px;
  border: 1px solid #333;
  padding: 1.5rem;
`;

interface TestCase {
  name: string;
  description: string;
  preset: Preset;
}

type TabType = 'positions' | 'testing';

const PositionsPage: React.FC = () => {
  // Positions state
  const [positions, setPositions] = useState<ExtendedPosition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [wsConnected, setWsConnected] = useState(false);
  
  // Testing state
  const [activeTab, setActiveTab] = useState<TabType>('positions');
  const [selectedTestCase, setSelectedTestCase] = useState<TestCase | null>(null);
  const [isRunningTest, setIsRunningTest] = useState(false);

  // Function to fetch positions from the API
  const fetchPositions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await positionService.getPositions();
      // Add symbol field (assuming coin field exists in API response)
      const extendedData = data.map(position => ({
        ...position,
        symbol: position.coin + '-PERP'
      }));
      setPositions(extendedData);
    } catch (err) {
      console.error('Error fetching positions:', err);
      setError('Failed to fetch positions. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Initialize websocket connection for position updates
  useEffect(() => {
    // Fetch initial positions
    fetchPositions();
    
    // Define the position update handler
    const handlePositionUpdate = (updatedPosition: ExtendedPosition) => {
      // Update positions with the updated data
      setPositions(prevPositions => {
        const symbolKey = updatedPosition.symbol || updatedPosition.coin + '-PERP';
        
        // Find if this position already exists in our list
        const existingIndex = prevPositions.findIndex(
          p => (p.symbol || p.coin + '-PERP') === symbolKey
        );
        
        if (existingIndex >= 0) {
          // Update existing position
          const newPositions = [...prevPositions];
          newPositions[existingIndex] = {
            ...updatedPosition,
            symbol: symbolKey // Ensure symbol is set
          };
          return newPositions;
        } else {
          // Add new position
          return [...prevPositions, {
            ...updatedPosition,
            symbol: symbolKey // Ensure symbol is set
          }];
        }
      });
    };

    // Connect to WebSocket for live updates
    const connect = async () => {
      try {
        await websocketService.connect();
        setWsConnected(true);
        
        // Set up message handler for position updates
        websocketService.on('positionUpdate', handlePositionUpdate);
      } catch (error) {
        console.error('WebSocket connection failed:', error);
        setWsConnected(false);
      }
    };
    
    connect();
    
    // Clean up on unmount
    return () => {
      // Remove the event listener
      websocketService.off('positionUpdate', handlePositionUpdate);
      websocketService.disconnect();
    };
  }, []);

  const handlePositionClick = (position: ExtendedPosition) => {
    console.log('Position clicked:', position);
  };

  // Sample test cases for various order types
  const testCases: TestCase[] = [
    {
      name: 'Market Buy',
      description: 'Test a simple market buy order with 1x leverage',
      preset: {
        id: '0',
        name: 'Test Market Buy',
        description: 'Test market buy order',
        symbol: 'BTC-PERP',
        order_type: 'market',
        side: 'buy',
        amount: 0.001,
        amount_type: 'base',
        margin_mode: 'cross',
        leverage: 1,
        limit_price: null,
        stop_price: null,
        take_profit: { enabled: false },
        stop_loss: { enabled: false },
        flags: { post_only: false, reduce_only: false },
        webhook_enabled: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        preset_type: 'preset'
      }
    },
    {
      name: 'Limit Sell at Best Bid',
      description: 'Test a limit sell order at best bid price',
      preset: {
        id: '1',
        name: 'Test Limit Sell Best Bid',
        description: 'Test limit sell at best bid',
        symbol: 'ETH-PERP',
        order_type: 'limit',
        side: 'sell',
        amount: 0.01,
        amount_type: 'base',
        margin_mode: 'cross',
        leverage: 2,
        limit_price_type: LimitPriceType.BEST_BID,
        price_offset: 0,
        stop_price: null,
        take_profit: { enabled: false },
        stop_loss: { enabled: false },
        flags: { post_only: true, reduce_only: false },
        webhook_enabled: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        preset_type: 'preset'
      }
    },
    {
      name: 'Stop Market with TP/SL',
      description: 'Test a stop market order with take profit and stop loss',
      preset: {
        id: '2',
        name: 'Test Stop Market with TP/SL',
        description: 'Test stop market with TP/SL',
        symbol: 'SOL-PERP',
        order_type: 'stop_market',
        side: 'buy',
        amount: 1,
        amount_type: 'base',
        margin_mode: 'isolated',
        leverage: 5,
        stop_price: 60,
        take_profit: { 
          enabled: true,
          trigger_price_value: 70
        },
        stop_loss: { 
          enabled: true,
          trigger_price_value: 55
        },
        flags: { post_only: false, reduce_only: false },
        webhook_enabled: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        preset_type: 'preset'
      }
    },
    {
      name: 'Layered Buy',
      description: 'Test a layered (scale) buy order',
      preset: {
        id: '3',
        name: 'Test Layered Buy',
        description: 'Test layered buy',
        symbol: 'BTC-PERP',
        order_type: 'layered',
        side: 'buy',
        amount: 0.003,
        amount_type: 'base',
        margin_mode: 'cross',
        leverage: 3,
        limit_price: 50000,
        limit_price_type: LimitPriceType.ABSOLUTE,
        layered: {
          parts: 3,
          step: 100
        },
        take_profit: { enabled: false },
        stop_loss: { enabled: false },
        flags: { post_only: false, reduce_only: false },
        webhook_enabled: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        preset_type: 'preset'
      }
    }
  ];

  const handleRunTest = (testCase: TestCase) => {
    setSelectedTestCase(testCase);
    setIsRunningTest(true);
    toast.info(`Loading test case: ${testCase.name}`);
  };

  const handleTestComplete = (success: boolean, message: string) => {
    setIsRunningTest(false);
    if (success) {
      toast.success(`Test completed: ${message}`);
      
      // Refresh positions after a successful test
      if (message.includes('placed successfully')) {
        setTimeout(() => {
          fetchPositions();
          toast.info('Refreshing positions...');
        }, 2000);
      }
    } else {
      toast.error(`Test failed: ${message}`);
    }
  };

  // Render positions tab content
  const renderPositions = () => {
    if (loading && positions.length === 0) {
      return <LoadingText>Loading positions...</LoadingText>;
    }

    if (error) {
      return <ErrorText>{error}</ErrorText>;
    }

    if (positions.length === 0) {
      return <LoadingText>No open positions found.</LoadingText>;
    }

    return (
      <PositionsContainer>
        {positions.map((position) => (
          <PositionCard
            key={position.id}
            position={position as any}
            onClick={() => handlePositionClick(position)}
          />
        ))}
      </PositionsContainer>
    );
  };

  // Render testing tab content
  const renderTesting = () => {
    return (
      <>
        <TestControls>
          {testCases.map(testCase => (
            <TestButton 
              key={testCase.name}
              onClick={() => handleRunTest(testCase)}
              disabled={isRunningTest}
            >
              {testCase.name}
            </TestButton>
          ))}
        </TestControls>
        
        {selectedTestCase && (
          <TestCard>
            <TestCardTitle>{selectedTestCase.name}</TestCardTitle>
            <TestDescription>{selectedTestCase.description}</TestDescription>
            
            <FormContainer>
              <OrderForm 
                selectedPreset={selectedTestCase.preset} 
                onOrderSubmitted={(success, message) => handleTestComplete(success, message)}
              />
            </FormContainer>
          </TestCard>
        )}
        
        {!selectedTestCase && (
          <TestDescription>
            Select a test case above to load the order form with predefined parameters.
            This allows you to test different order types with the backend.
          </TestDescription>
        )}
      </>
    );
  };

  return (
    <PageContainer>
      <Header>
        <Title>Positions</Title>
        <div>
          {wsConnected ? (
            <span style={{ color: '#10b981', fontSize: '0.9rem' }}>● Live updates connected</span>
          ) : (
            <span style={{ color: '#f87171', fontSize: '0.9rem' }}>● Live updates disconnected</span>
          )}
        </div>
      </Header>

      <TabContainer>
        <Tab active={activeTab === 'positions'} onClick={() => setActiveTab('positions')}>
          Open Positions
        </Tab>
        <Tab active={activeTab === 'testing'} onClick={() => setActiveTab('testing')}>
          Order Testing
        </Tab>
      </TabContainer>

      {activeTab === 'positions' ? renderPositions() : renderTesting()}
    </PageContainer>
  );
};

export default PositionsPage; 